import React, { useState } from "react";
import {
    MdArrowForward,
    MdCheckCircleOutline,
    MdErrorOutline,
    MdVpnKey,
} from "react-icons/md";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";

import {
    doc,
    runTransaction,
    serverTimestamp,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import logoLight from "../../assets/logo1.png";

const InviteScreen = () => {
    const params = useParams();

    const parkId = params.parkId || params.id;

    const [invitationCode, setInvitationCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [success, setSuccess] = useState({
        visible: false,
        action: "",
        message: "",
    });

    const normalizeCode = (value) => {
        return String(value || "")
            .trim()
            .replace(/\s+/g, "")
            .toUpperCase();
    };

    const createCommandId = () => {
        if (
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
        ) {
            return crypto.randomUUID();
        }

        return `${Date.now()}_${Math.random()
            .toString(36)
            .slice(2, 10)}`;
    };

    const handleCodeChange = (event) => {
        setInvitationCode(event.target.value);

        if (error) {
            setError("");
        }

        if (success.visible) {
            setSuccess({
                visible: false,
                action: "",
                message: "",
            });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (loading) return;

        const normalizedCode = normalizeCode(invitationCode);

        if (!normalizedCode) {
            setError("Урилгын кодоо оруулна уу.");
            return;
        }
        const validCodePattern = /^[\p{L}\p{N}_-]{4,30}$/u;

        if (!validCodePattern.test(normalizedCode)) {
            setError("Урилгын кодын формат буруу байна.");
            return;
        }

        if (!parkId) {
            setError("Зогсоолын мэдээлэл олдсонгүй.");
            return;
        }
        setError("");
        setLoading(true);

        try {
            const invitationRef = doc(
                db,
                "parkings",
                parkId,
                "invitations",
                normalizedCode
            );

            const parkingRef = doc(
                db,
                "parkings",
                parkId
            );

            const commandId = createCommandId();

            const result = await runTransaction(
                db,
                async (transaction) => {
                    /*
                     * Зөвхөн invitation document-ийг уншина.
                     * Parking document-ийг тусад нь унших шаардлагагүй.
                     */
                    const invitationSnapshot =
                        await transaction.get(invitationRef);

                    if (!invitationSnapshot.exists()) {
                        throw new Error("INVITATION_NOT_FOUND");
                    }

                    const invitationData =
                        invitationSnapshot.data();

                    const storedCode = normalizeCode(
                        invitationData.code || normalizedCode
                    );

                    if (storedCode !== normalizedCode) {
                        throw new Error("INVALID_INVITATION");
                    }

                    /*
                     * Хугацаатай урилга ашиглаж байгаа бол шалгана.
                     * expiresAt байхгүй бол хугацаагүй гэж үзнэ.
                     */
                    if (invitationData.expiresAt) {
                        const expiresAt =
                            invitationData.expiresAt.toDate
                                ? invitationData.expiresAt.toDate()
                                : new Date(invitationData.expiresAt);

                        if (
                            !Number.isNaN(expiresAt.getTime()) &&
                            expiresAt.getTime() < Date.now()
                        ) {
                            throw new Error("INVITATION_EXPIRED");
                        }
                    }

                    /*
                     * Хуучин document status-гүй байж болохыг дэмжинэ:
                     *
                     * isUsed=false → unused
                     * isUsed=true  → inside
                     */
                    const currentStatus =
                        invitationData.status ||
                        (
                            invitationData.isUsed === true
                                ? "inside"
                                : "unused"
                        );

                    /*
                     * 1-р ашиглалт: ОРОХ
                     */
                    if (currentStatus === "unused") {
                        transaction.update(invitationRef, {
                            code: normalizedCode,
                            isUsed: true,
                            status: "inside",
                            enteredAt: serverTimestamp(),
                            enterCommandId: commandId,
                        });

                        transaction.update(parkingRef, {
                            "gate.enterOpen": true,

                        });

                        return {
                            action: "enter",
                        };
                    }

                    /*
                     * 2-р ашиглалт: ГАРАХ
                     */
                    if (currentStatus === "inside") {
                        transaction.update(invitationRef, {
                            isUsed: true,
                            status: "exited",
                            exitedAt: serverTimestamp(),
                            exitCommandId: commandId,
                        });

                        transaction.update(parkingRef, {
                            "gate.exitOpen": true,
                        });

                        return {
                            action: "exit",
                        };
                    }
                    /*
                     * Орох болон гарахад ашигласан.
                     */
                    if (currentStatus === "exited") {
                        throw new Error(
                            "INVITATION_ALREADY_COMPLETED"
                        );
                    }

                    throw new Error(
                        "INVALID_INVITATION_STATUS"
                    );
                }
            );

            if (result.action === "enter") {
                setSuccess({
                    visible: true,
                    action: "enter",
                    message: "Орох хаалга нээгдлээ.",
                });
            }

            if (result.action === "exit") {
                setSuccess({
                    visible: true,
                    action: "exit",
                    message: "Гарах хаалга нээгдлээ.",
                });
            }

            setInvitationCode("");
        } catch (err) {
            console.error(
                "Invitation submit error:",
                err
            );

            if (
                err.message ===
                "INVITATION_ALREADY_COMPLETED"
            ) {
                setError(
                    "Энэ урилгын кодыг ашигласан байна."
                );
            } else if (
                err.message === "INVITATION_NOT_FOUND" ||
                err.message === "INVALID_INVITATION"
            ) {
                setError("Урилгын код буруу байна.");
            } else if (
                err.message === "INVITATION_EXPIRED"
            ) {
                setError(
                    "Энэ урилгын кодын хүчинтэй хугацаа дууссан байна."
                );
            } else if (
                err.message ===
                "INVALID_INVITATION_STATUS"
            ) {
                setError(
                    "Урилгын кодын төлөв буруу байна."
                );
            } else if (
                err.code === "permission-denied"
            ) {
                setError(
                    "Хаалга удирдах эрх хүрэлцэхгүй байна."
                );
            } else if (
                err.code === "not-found"
            ) {
                setError(
                    "Зогсоолын тохиргоо олдсонгүй."
                );
            } else {
                setError(
                    "Код шалгах үед алдаа гарлаа. Дахин оролдоно уу."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="tw-fixed tw-inset-0 tw-z-[99999] tw-flex tw-min-h-screen tw-w-full tw-items-center tw-justify-center tw-bg-[#0d1117] tw-p-4">
            <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-h-[320px] tw-w-[320px] tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-full tw-bg-blue-500/10 tw-blur-[100px]" />

            <motion.section
                initial={{
                    opacity: 0,
                    y: 15,
                }}
                animate={{
                    opacity: 1,
                    y: 0,
                }}
                transition={{
                    duration: 0.3,
                }}
                className="tw-relative tw-z-10 tw-w-full tw-max-w-[390px] tw-rounded-3xl tw-border tw-border-[#2f343a] tw-bg-[#161b22] tw-p-6 tw-shadow-[0_20px_60px_rgba(0,0,0,0.45)] sm:tw-p-8"
            >
                <div className="tw-mb-7 tw-flex tw-justify-center">
                    <img
                        src={logoLight}
                        alt="iParking"
                        className="tw-h-20 tw-w-auto tw-object-contain"
                    />
                </div>

                <div className="tw-mb-7 tw-text-center">
                    <h1 className="tw-text-2xl tw-font-black tw-text-white">
                        Урилгын код
                    </h1>

                    <p className="tw-mt-2 tw-text-sm tw-leading-6 tw-text-[#8b949e]">
                        Орох эсвэл гарах урилгын кодоо оруулна уу.
                    </p>
                </div>
                {success.visible && (
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: -5,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        className="tw-mb-5 tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-emerald-500/30 tw-bg-emerald-500/10 tw-p-4"
                    >
                        <MdCheckCircleOutline
                            size={24}
                            className="tw-shrink-0 tw-text-emerald-400"
                        />

                        <div>
                            <p className="tw-text-sm tw-font-bold tw-text-emerald-400">
                                Амжилттай
                            </p>

                            <p className="tw-mt-0.5 tw-text-sm tw-text-[#c9d1d9]">
                                {success.message}
                            </p>
                        </div>
                    </motion.div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                    <label
                        htmlFor="invitationCode"
                        className="tw-mb-2 tw-block tw-text-sm tw-font-semibold tw-text-[#c9d1d9]"
                    >
                        Код
                    </label>

                    <div
                        className={`
                            tw-flex
                            tw-h-14
                            tw-items-center
                            tw-rounded-xl
                            tw-border
                            tw-bg-[#0d1117]
                            tw-transition-all
                            focus-within:tw-ring-4
                            ${
                                error
                                    ? "tw-border-red-500 focus-within:tw-ring-red-500/10"
                                    : "tw-border-[#30363d] focus-within:tw-border-blue-500 focus-within:tw-ring-blue-500/10"
                            }
                        `}
                    >
                        <MdVpnKey
                            size={21}
                            className={`tw-ml-4 tw-shrink-0 ${
                                error
                                    ? "tw-text-red-400"
                                    : "tw-text-[#6e7681]"
                            }`}
                        />

                        <input
                            id="invitationCode"
                            type="text"
                            value={invitationCode}
                            onChange={handleCodeChange}
                            placeholder="Урилгын код"
                            autoComplete="off"
                            autoCapitalize="characters"
                            autoFocus
                            disabled={loading}
                            maxLength={30}
                            aria-invalid={Boolean(error)}
                            className="
                                tw-h-full
                                tw-w-full
                                tw-bg-transparent
                                tw-px-4
                                tw-text-base
                                tw-font-bold
                                tw-uppercase
                                tw-tracking-wider
                                tw-text-white
                                tw-outline-none
                                placeholder:tw-normal-case
                                placeholder:tw-font-normal
                                placeholder:tw-tracking-normal
                                placeholder:tw-text-[#484f58]
                                disabled:tw-cursor-not-allowed
                                disabled:tw-opacity-60
                            "
                        />
                    </div>

                    <div className="tw-min-h-[42px] tw-pt-2">
                        {error && (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-red-400">
                                <MdErrorOutline
                                    size={18}
                                    className="tw-shrink-0"
                                />

                                <span>{error}</span>
                            </div>
                        )}
                    </div>

                    <motion.button
                        type="submit"
                        whileTap={
                            loading
                                ? undefined
                                : {
                                      scale: 0.98,
                                  }
                        }
                        disabled={loading}
                        className="
                            tw-flex
                            tw-h-14
                            tw-w-full
                            tw-items-center
                            tw-justify-center
                            tw-gap-2
                            tw-rounded-xl
                            tw-bg-blue-600
                            tw-px-5
                            tw-text-base
                            tw-font-bold
                            tw-text-white
                            tw-transition-colors
                            hover:tw-bg-blue-700
                            disabled:tw-cursor-not-allowed
                            disabled:tw-opacity-60
                        "
                    >
                        {loading ? (
                            <>
                                <span className="tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-white/30 tw-border-t-white" />
                                <span>Шалгаж байна...</span>
                            </>
                        ) : (
                            <>
                                <span>Нэвтрэх</span>
                                <MdArrowForward size={20} />
                            </>
                        )}
                    </motion.button>
                </form>

                <p className="tw-mt-6 tw-text-center tw-text-xs tw-text-[#6e7681]">
                    Кодоо оруулаад{" "}
                    <kbd className="tw-rounded tw-border tw-border-[#30363d] tw-bg-[#21262d] tw-px-2 tw-py-1 tw-text-[#c9d1d9]">
                        Enter
                    </kbd>{" "}
                    дарна уу
                </p>
            </motion.section>
        </main>
    );
};



export default InviteScreen;

